import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { aiText, aiImage, aiVoice, aiVideo, videoJob, aiHistory, downloadFromUrl, uploadFile, absFileUrl, logSaveConsent } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { useLang } from "../../i18n/LanguageContext";
import { scrollToId } from "../../hooks/useLenis";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { Sparkles, Loader2, Copy, Download, Clock, Coins, LogIn, ImagePlus, X, ShieldCheck, FileText, FileType, RotateCcw } from "lucide-react";

const exportTxt = (text, name = "aiba-document") => {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${name}.txt`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

const exportPdf = (text, name = "aiba-document") => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const margin = 48;
  const width = doc.internal.pageSize.getWidth() - margin * 2;
  const pageH = doc.internal.pageSize.getHeight() - margin;
  doc.setFont("helvetica", "normal"); doc.setFontSize(11);
  const lines = doc.splitTextToSize(text, width);
  let y = margin;
  lines.forEach((line) => {
    if (y > pageH) { doc.addPage(); y = margin; }
    doc.text(line, margin, y); y += 16;
  });
  doc.save(`${name}.pdf`);
};

const fileToDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

const MODELS = [
  { value: "claude", label: "Claude Sonnet 4.6" },
  { value: "gpt", label: "GPT-5.4" },
  { value: "gemini", label: "Gemini 3.1 Pro" },
  { value: "gemini-flash", label: "Gemini 3.5 Flash" },
];
const VOICES = ["onyx", "nova", "shimmer", "echo", "sage", "alloy"];
const VIDEO_DURATIONS = [5, 10, 15, 20, 30];
const VIDEO_COST_PER_SEC = 6;
const COST = { text: 5, image: 20, voice: 15 };

export const ToolConsole = ({ tool, open, onClose, initialPrompt = "" }) => {
  const { user, setAuthOpen, setCredits } = useAuth();
  const { t } = useLang();
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("claude");
  const [voice, setVoice] = useState("onyx");
  const [duration, setDuration] = useState(5);
  const [videoEngine, setVideoEngine] = useState("seedance");
  const [meta, setMeta] = useState({});
  const [consent, setConsent] = useState(false);
  const [refImage, setRefImage] = useState(null);     // base64 data URL for text vision
  const [videoImg, setVideoImg] = useState(null);     // uploaded public URL for image-to-video
  const [videoElapsed, setVideoElapsed] = useState(0);
  const [imgBusy, setImgBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("console");
  const [messages, setMessages] = useState([]);      // text conversation thread
  const [sessionId, setSessionId] = useState(null);   // conversation session id
  const [saveConsent, setSaveConsent] = useState(false); // consent to save/export content

  useEffect(() => {
    if (open && tool) {
      setPrompt(initialPrompt || "");
      setResult(null);
      setMeta({});
      setConsent(false);
      setRefImage(null);
      setVideoImg(null);
      setTab("console");
      setMessages([]);
      setSessionId(null);
      setSaveConsent(false);
    }
  }, [open, tool, initialPrompt]);

  useEffect(() => {
    if (tab === "history" && tool && user) {
      aiHistory(tool.id).then(({ data }) => setHistory(data)).catch(() => {});
    }
  }, [tab, tool, user]);

  if (!tool) return null;
  const cost = tool.kind === "video" ? duration * VIDEO_COST_PER_SEC : COST[tool.kind];
  const needsConsent = tool.id === "clone" || tool.id === "ugc";

  const buyCredits = () => {
    onClose();
    setTimeout(() => scrollToId("credits"), 200);
  };

  const doExport = async (fmt) => {
    const transcript = messages
      .map((m) => `${m.role === "user" ? "Vous" : tool.name} :\n${m.content}`)
      .join("\n\n----------------------------------------\n\n");
    const name = `aiba-${tool.id}-${new Date().toISOString().slice(0, 10)}`;
    if (fmt === "pdf") exportPdf(transcript, name); else exportTxt(transcript, name);
    try { await logSaveConsent(tool.id, fmt); } catch (e) { console.error("save consent log failed", e); }
    toast.success(`Document enregistré (.${fmt})`);
  };

  const pickTextImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error("Image trop lourde (max 6 Mo)."); return; }
    try {
      setRefImage(await fileToDataUrl(f));
      setModel("gemini-flash"); // Gemini vision handles uploaded images reliably
      toast.info("Image ajoutée — analyse via Gemini.");
    } catch { toast.error("Image illisible."); }
  };
  const pickVideoImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 20 * 1024 * 1024) { toast.error("Image trop lourde (max 20 Mo)."); return; }
    try { setImgBusy(true); const { data } = await uploadFile(f); setVideoImg(absFileUrl(data.path)); }
    catch { toast.error("Upload impossible."); } finally { setImgBusy(false); }
  };

  const pollVideoJob = (jobId) =>
    new Promise((resolve, reject) => {
      const started = Date.now();
      const iv = setInterval(async () => {
        setVideoElapsed(Math.round((Date.now() - started) / 1000));
        try {
          const { data } = await videoJob(jobId);
          if (data.status !== "processing") {
            clearInterval(iv);
            resolve(data);
          } else if (Date.now() - started > 8 * 60 * 1000) {
            clearInterval(iv);
            reject(new Error("timeout"));
          }
        } catch (err) {
          clearInterval(iv);
          reject(err);
        }
      }, 4000);
    });

  const generate = async () => {
    if (!user) {
      onClose();
      setAuthOpen(true);
      return;
    }
    const hasImage = (tool.kind === "text" && refImage) || (tool.kind === "video" && videoImg);
    if (!prompt.trim() && !hasImage) {
      toast.error(tool.kind === "video" ? "Décrivez la vidéo ou ajoutez une image." : "Entrez une instruction.");
      return;
    }
    if (user.credits < cost) {
      toast.error("Crédits insuffisants.");
      return;
    }
    if (needsConsent && !consent) {
      toast.error("Cochez la case de consentement avant de générer.");
      return;
    }
    setLoading(true);
    setResult(null);
    setVideoElapsed(0);
    try {
      let finalPrompt = prompt;
      if (tool.fields?.length) {
        const parts = tool.fields.filter((fld) => meta[fld.key]).map((fld) => `${fld.label} : ${meta[fld.key]}`);
        if (parts.length) finalPrompt = `[Clone IA — ${parts.join(", ")}] ${prompt}`;
      }
      if (tool.kind === "video") {
        // Async job: start generation, then poll until completed/failed (avoids gateway timeout).
        const { data: start } = await aiVideo(finalPrompt, duration, videoImg, videoEngine);
        if (typeof start.credits === "number") setCredits(start.credits);
        const job = await pollVideoJob(start.job_id);
        if (typeof job.credits === "number") setCredits(job.credits);
        if (job.status === "failed") {
          toast.error(job.error || "Échec de génération vidéo. Crédits remboursés.");
          return;
        }
        setResult({ kind: "video", video: job.video });
        toast.success(`${tool.name} : vidéo générée · -${cost} crédits`);
        return;
      }
      if (tool.kind === "text") {
        const userText = prompt;
        const userImg = refImage;
        const { data } = await aiText(tool.id, finalPrompt, model, refImage, sessionId);
        setMessages((m) => [...m,
          { role: "user", content: userText, image: userImg },
          { role: "assistant", content: data.text }]);
        setSessionId(data.session_id);
        if (typeof data.credits === "number") setCredits(data.credits);
        setPrompt("");
        setRefImage(null);
        toast.success(`${tool.name} · -${cost} crédits`);
        return;
      }
      let data;
      if (tool.kind === "image") ({ data } = await aiImage(tool.id, finalPrompt, consent));
      else if (tool.kind === "voice") ({ data } = await aiVoice(finalPrompt, voice));
      setResult({ kind: tool.kind, ...data });
      if (typeof data.credits === "number") setCredits(data.credits);
      toast.success(`${tool.name} : génération réussie · -${cost} crédits`);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Crédits insuffisants pour cette génération.");
      } else if (e.response?.status === 403) {
        const d = e.response?.data?.detail;
        toast.error((d && (d.message || (typeof d === "string" && d))) || "Cet outil nécessite un abonnement supérieur.");
      } else if (e.response?.status === 503) {
        toast.error("Génération vidéo indisponible (solde du fournisseur épuisé).");
      } else if (e.message === "timeout") {
        toast.error("La génération vidéo prend trop de temps. Réessayez avec une durée plus courte.");
      } else {
        toast.error(e.response?.data?.detail || "Échec de la génération. Réessayez.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="tool-console" className="max-h-[88vh] max-w-2xl overflow-hidden border-blue-500/25 bg-[#0B1220]/95 p-0 backdrop-blur-2xl">
        <DialogHeader className="border-b border-blue-500/15 px-6 py-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-blue-400/40 bg-blue-400/10 font-mono text-[10px] text-blue-200">{tool.code}</span>
              <div>
                <DialogTitle className="font-display text-lg font-700 tracking-tighter text-white">{tool.name}</DialogTitle>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-300/70">{tool.tagline}</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 rounded-full border border-blue-500/20 px-3 py-1.5">
              <Coins size={12} className="text-blue-300" />
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-blue-200">{cost} {t("console.perGen")}</span>
            </div>
          </div>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="px-6 pt-4">
          <TabsList className="bg-blue-400/5">
            <TabsTrigger value="console" data-testid="console-tab-generate" className="font-mono text-[11px] uppercase tracking-[0.15em]">Console</TabsTrigger>
            <TabsTrigger value="history" data-testid="console-tab-history" className="font-mono text-[11px] uppercase tracking-[0.15em]">{t("console.history")}</TabsTrigger>
          </TabsList>

          <TabsContent value="console" className="mt-4 max-h-[62vh] overflow-y-auto pb-6">
            {!user && (
              <div data-testid="console-login-banner" className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-blue-400/40 bg-blue-500/10 p-4">
                <span className="font-body text-xs text-[#CBD5E1]">{t("console.loginBanner")}</span>
                <button onClick={() => { onClose(); setAuthOpen(true); }} className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-blue-600 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-white">
                  <LogIn size={12} /> {t("console.loginBtn")}
                </button>
              </div>
            )}
            {tool.fields?.length > 0 && (
              <div data-testid="console-clone-fields" className="mb-4 grid grid-cols-2 gap-3 rounded-xl border border-blue-500/15 bg-blue-400/[0.02] p-4">
                <p className="col-span-2 font-mono text-[10px] uppercase tracking-[0.25em] text-blue-300/70">{t("console.cloneIdentity")}</p>
                {tool.fields.map((fld) => (
                  <div key={fld.key} className="flex flex-col gap-1">
                    <label className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#8AA0C0]">{fld.label}</label>
                    {fld.type === "select" ? (
                      <select
                        data-testid={`clone-field-${fld.key}`}
                        value={meta[fld.key] || ""}
                        onChange={(e) => setMeta((m) => ({ ...m, [fld.key]: e.target.value }))}
                        className="rounded-lg border border-blue-500/25 bg-[#0B1220] px-3 py-2 text-sm text-white outline-none focus:border-blue-400/40"
                      >
                        <option value="">—</option>
                        {fld.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input
                        data-testid={`clone-field-${fld.key}`}
                        type={fld.type}
                        value={meta[fld.key] || ""}
                        placeholder={fld.placeholder}
                        onChange={(e) => setMeta((m) => ({ ...m, [fld.key]: e.target.value }))}
                        className="rounded-lg border border-blue-500/25 bg-transparent px-3 py-2 text-sm text-white outline-none placeholder:text-[#405060] focus:border-blue-400/40"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-blue-300/70">{tool.promptLabel}</label>
            <Textarea
              data-testid="console-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={tool.kind === "text" && messages.length > 0 ? "Poursuivez la conversation…" : tool.placeholder}
              rows={3}
              className="mt-2 resize-none border-blue-500/25 bg-transparent text-white placeholder:text-[#405060] focus-visible:ring-blue-500/40"
            />

            {(tool.kind === "text" || tool.kind === "video") && (
              <div className="mt-3" data-testid="console-image-input-wrap">
                {tool.kind === "text" && refImage ? (
                  <div className="relative inline-block">
                    <img src={refImage} alt="Référence" className="h-20 w-20 rounded-lg border border-blue-500/25 object-cover" />
                    <button data-testid="console-remove-image" onClick={() => setRefImage(null)} className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-blue-400/40 bg-[#0B1220] text-blue-200"><X size={12} /></button>
                  </div>
                ) : tool.kind === "video" && videoImg ? (
                  <div className="relative inline-block">
                    <img src={videoImg} alt="Image de départ" className="h-20 w-20 rounded-lg border border-blue-500/25 object-cover" />
                    <button data-testid="console-remove-image" onClick={() => setVideoImg(null)} className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-blue-400/40 bg-[#0B1220] text-blue-200"><X size={12} /></button>
                  </div>
                ) : (
                  <label data-testid="console-image-label" className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-blue-500/20 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-blue-200 hover:border-blue-400/40">
                    {imgBusy ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
                    {tool.kind === "video" ? t("console.imgToVideo") : t("console.addImage")}
                    <input data-testid="console-image-file" type="file" accept="image/*" onChange={tool.kind === "video" ? pickVideoImage : pickTextImage} className="hidden" />
                  </label>
                )}
              </div>
            )}

            {needsConsent && (
              <label data-testid="console-consent" className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl border border-amber-400/30 bg-amber-400/[0.05] p-4">
                <input
                  data-testid="console-consent-checkbox"
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-amber-400"
                />
                <span className="font-body text-[11px] leading-relaxed text-[#CBD5E1]">
                  <ShieldCheck size={12} className="mr-1 inline text-amber-300" />
                  Je certifie détenir tous les droits et consentements nécessaires sur les personnes / données utilisées, et je m'engage à ne créer aucun contenu trompeur, diffamatoire ou non autorisé. J'accepte les{" "}
                  <a href="/legal" target="_blank" rel="noopener noreferrer" data-testid="console-consent-link" className="text-amber-300 underline hover:text-amber-200">conditions d'utilisation</a>.
                </span>
              </label>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-3">
              {tool.kind === "text" && (
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger data-testid="console-model-select" className="h-9 w-48 border-blue-500/25 bg-transparent font-mono text-[11px] text-blue-100"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-blue-500/25 bg-[#0B1220] text-white">
                    {MODELS.map((m) => <SelectItem key={m.value} value={m.value} className="font-mono text-xs">{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {tool.kind === "voice" && (
                <Select value={voice} onValueChange={setVoice}>
                  <SelectTrigger data-testid="console-voice-select" className="h-9 w-40 border-blue-500/25 bg-transparent font-mono text-[11px] text-blue-100"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-blue-500/25 bg-[#0B1220] text-white">
                    {VOICES.map((v) => <SelectItem key={v} value={v} className="font-mono text-xs capitalize">{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {tool.kind === "video" && (
                <Select value={videoEngine} onValueChange={setVideoEngine}>
                  <SelectTrigger data-testid="console-engine-select" className="h-9 w-52 border-blue-500/25 bg-transparent font-mono text-[11px] text-blue-100"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-blue-500/25 bg-[#0B1220] text-white">
                    <SelectItem value="seedance" className="font-mono text-xs">Seedance · Cinématique</SelectItem>
                    <SelectItem value="wan" className="font-mono text-xs">WAN v2.7 · Rapide</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {tool.kind === "video" && (
                <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
                  <SelectTrigger data-testid="console-duration-select" className="h-9 w-44 border-blue-500/25 bg-transparent font-mono text-[11px] text-blue-100"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-blue-500/25 bg-[#0B1220] text-white">
                    {VIDEO_DURATIONS.map((d) => <SelectItem key={d} value={String(d)} className="font-mono text-xs">{d} secondes</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <button
                data-testid="console-generate-btn"
                onClick={generate}
                disabled={loading || (needsConsent && !consent)}
                className="inline-flex items-center gap-2 rounded-full bg-blue-500 px-6 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white cta-glow transition-all duration-300 hover:scale-[1.02] disabled:opacity-60"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {loading ? (tool.kind === "video" ? `${t("console.generatingVideo")}${videoElapsed ? ` · ${videoElapsed}s` : ""}` : t("console.generating")) : `${tool.kind === "text" && messages.length > 0 ? "Envoyer" : t("console.generate")} · ${cost} cr`}
              </button>
              {user && (
                <button data-testid="console-buy-credits" onClick={buyCredits} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-blue-300 hover:text-blue-200">
                  <Coins size={12} /> {user.credits} cr · recharger
                </button>
              )}
            </div>

            {tool.kind === "text" && messages.length > 0 && (
              <div className="mt-6" data-testid="console-conversation">
                <div className="max-h-[40vh] space-y-3 overflow-y-auto pr-1">
                  {messages.map((m, i) => (
                    <div
                      key={i}
                      data-testid={`console-msg-${m.role}`}
                      className={`rounded-xl border p-4 ${m.role === "user" ? "border-blue-500/20 bg-blue-400/[0.04]" : "border-blue-400/30 bg-[#0B1220]"}`}
                    >
                      <div className="mb-1.5 flex items-center justify-between">
                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-blue-300/70">{m.role === "user" ? "Vous" : tool.name}</span>
                        {m.role === "assistant" && (
                          <button data-testid="console-copy-msg" onClick={() => { navigator.clipboard.writeText(m.content); toast.success("Copié"); }} className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.15em] text-blue-300 hover:text-blue-200">
                            <Copy size={11} /> Copier
                          </button>
                        )}
                      </div>
                      {m.image && <img src={m.image} alt="" className="mb-2 h-20 w-20 rounded-lg border border-blue-500/20 object-cover" />}
                      <pre className="whitespace-pre-wrap font-body text-sm leading-relaxed text-[#cdd9e8]">{m.content}</pre>
                    </div>
                  ))}
                </div>

                <div className="mt-4 rounded-xl border border-blue-500/15 bg-blue-400/[0.02] p-4" data-testid="console-save-block">
                  <label data-testid="console-save-consent" className="flex cursor-pointer items-start gap-3">
                    <input
                      data-testid="console-save-consent-checkbox"
                      type="checkbox"
                      checked={saveConsent}
                      onChange={(e) => setSaveConsent(e.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 accent-blue-500"
                    />
                    <span className="font-body text-[11px] leading-relaxed text-[#CBD5E1]">
                      <ShieldCheck size={12} className="mr-1 inline text-blue-300" />
                      J'accepte d'enregistrer ce contenu et je certifie en détenir les droits d'utilisation.
                    </span>
                  </label>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      data-testid="console-export-txt"
                      disabled={!saveConsent}
                      onClick={() => doExport("txt")}
                      className="inline-flex items-center gap-1.5 rounded-full border border-blue-500/25 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-blue-200 transition-all duration-300 hover:bg-blue-400/10 disabled:opacity-40"
                    >
                      <FileText size={12} /> Enregistrer .txt
                    </button>
                    <button
                      data-testid="console-export-pdf"
                      disabled={!saveConsent}
                      onClick={() => doExport("pdf")}
                      className="inline-flex items-center gap-1.5 rounded-full border border-blue-500/25 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-blue-200 transition-all duration-300 hover:bg-blue-400/10 disabled:opacity-40"
                    >
                      <FileType size={12} /> Enregistrer .pdf
                    </button>
                  </div>
                </div>
              </div>
            )}

            {result && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="console-result" className="mt-5 rounded-xl border border-blue-500/20 bg-blue-400/[0.03] p-4">
                {result.kind === "text" && (
                  <>
                    <div className="mb-3 flex justify-end">
                      <button data-testid="console-copy-btn" onClick={() => { navigator.clipboard.writeText(result.text); toast.success("Copié"); }} className="inline-flex items-center gap-1.5 rounded-full border border-blue-500/25 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-blue-200 hover:bg-blue-400/10">
                        <Copy size={12} /> Copier
                      </button>
                    </div>
                    <pre className="whitespace-pre-wrap font-body text-sm leading-relaxed text-[#cdd9e8]">{result.text}</pre>
                  </>
                )}
                {result.kind === "image" && (
                  <div>
                    <img src={result.image} alt="Généré par AIBA" className="w-full rounded-lg border border-blue-500/20" />
                    <button data-testid="console-save-image" onClick={async () => { try { await downloadFromUrl(result.image, "aiba-image.png"); toast.success("Image enregistrée"); } catch { toast.error("Enregistrement impossible."); } }} className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-blue-500/25 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-blue-200 hover:bg-blue-400/10"><Download size={12} /> Enregistrer</button>
                  </div>
                )}
                {result.kind === "voice" && <audio controls src={result.audio} className="w-full" data-testid="console-audio" />}
                {result.kind === "video" && (
                  <div>
                    <video src={result.video} controls className="w-full rounded-lg border border-blue-500/20" data-testid="console-video" />
                    <button data-testid="console-save-video" onClick={async () => { try { await downloadFromUrl(result.video, "aiba-video.mp4"); toast.success("Vidéo enregistrée"); } catch { toast.error("Enregistrement impossible."); } }} className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-blue-500/25 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-blue-200 hover:bg-blue-400/10"><Download size={12} /> Enregistrer</button>
                  </div>
                )}
              </motion.div>
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-4 max-h-[62vh] space-y-3 overflow-y-auto pb-6">
            {(!user || history.length === 0) && (
              <p className="py-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-[#405060]">
                {user ? "Aucune génération pour l'instant" : "Connectez-vous pour voir votre historique"}
              </p>
            )}
            {user && history.map((h) => (
              <div key={h.id} className="rounded-xl border border-blue-500/15 bg-blue-400/[0.02] p-4" data-testid="history-item">
                <div className="mb-2 flex items-center gap-2 text-[#8AA0C0]">
                  <Clock size={11} />
                  <span className="font-mono text-[9px] uppercase tracking-[0.15em]">{new Date(h.created_at).toLocaleString("fr-FR")}</span>
                </div>
                <p className="font-body text-xs text-[#8AA0C0]">{h.prompt?.slice(0, 120)}</p>
                {h.kind === "text" && <p className="mt-2 line-clamp-3 whitespace-pre-wrap font-body text-xs text-[#cdd9e8]">{h.text}</p>}
                {h.kind === "image" && h.media && (
                  <div className="mt-2">
                    <img src={h.media} alt="" className="h-24 w-24 rounded-lg border border-blue-500/20 object-cover" />
                    <button data-testid="history-save-image" onClick={async () => { try { await downloadFromUrl(h.media, "aiba-image.png"); toast.success("Image enregistrée"); } catch { toast.error("Enregistrement impossible."); } }} className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-blue-500/25 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-blue-200 hover:bg-blue-400/10"><Download size={11} /> Enregistrer</button>
                  </div>
                )}
                {h.kind === "voice" && h.media && (
                  <div className="mt-2">
                    <audio controls src={h.media} className="w-full" />
                    <button data-testid="history-save-voice" onClick={async () => { try { await downloadFromUrl(h.media, "aiba-voix.mp3"); toast.success("Audio enregistré"); } catch { toast.error("Enregistrement impossible."); } }} className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-blue-500/25 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-blue-200 hover:bg-blue-400/10"><Download size={11} /> Enregistrer</button>
                  </div>
                )}
                {h.kind === "video" && h.media && (
                  <div className="mt-2">
                    <video controls src={h.media} className="w-full rounded-lg border border-blue-500/20" />
                    <button data-testid="history-save-video" onClick={async () => { try { await downloadFromUrl(h.media, "aiba-video.mp4"); toast.success("Vidéo enregistrée"); } catch { toast.error("Enregistrement impossible."); } }} className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-blue-500/25 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-blue-200 hover:bg-blue-400/10"><Download size={11} /> Enregistrer</button>
                  </div>
                )}
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
